// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens + glass-card chrome; data layer (queries, mutations)
// untouched.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as pb from '@/generated/console_pb'
import {
  createApiKey,
  createOperator,
  disableOperator,
  listApiKeys,
  listOperators,
  revokeApiKey,
} from '@/lib/grpcweb/console-api'
import { useAuth } from '@/lib/auth/auth-context'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ShowOnceKeyModal } from '@/components/ShowOnceKeyModal'
import { formatTimestamp } from '@/lib/formatters'
import { DANGER, DARK, GREY_300, GREY_400, GREY_500, SUCCESS, WARNING, WHITE, primaryButtonCompactStyle } from '@/lib/genesis'

export function OperatorsPage() {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['operators'],
    queryFn: () => listOperators({}),
  })

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<pb.ConsoleOperatorRole>(
    pb.ConsoleOperatorRole.CONSOLE_OPERATOR_ROLE_VIEWER,
  )

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['operators'] })

  const createOpMutation = useMutation({
    mutationFn: () => createOperator({ name, email, role }),
    onSuccess: () => {
      setName('')
      setEmail('')
      invalidate()
    },
  })

  // Show-once modal state: holds the plaintext key ONLY between
  // CreateApiKey's response and the operator explicitly closing the
  // modal. Cleared (not just hidden) on close so it cannot be
  // accidentally re-rendered from stale state (task brief step 5: "closing
  // it discards the value from app state").
  const [shownKey, setShownKey] = useState<string | null>(null)

  if (!isAdmin) {
    return (
      <div>
        <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Operators</h1>
        <div className="px-3 py-2" style={{ backgroundColor: DARK, borderLeft: `3px solid ${WARNING}`, borderRadius: '8px' }}>
          <p className="text-sm" style={{ color: WHITE }}>
            Operator management is admin-only. ListOperators itself requires
            admin (ADR-119 D2 — it enumerates platform-level identity), so a
            viewer-role session cannot see this page's data at all.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Operators</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          createOpMutation.mutate()
        }}
        className="glass-card mb-6 flex flex-wrap items-end gap-3 p-4"
      >
        <div>
          <label htmlFor="operator-name" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Name
          </label>
          <input
            id="operator-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="glass-input px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          />
        </div>
        <div>
          <label htmlFor="operator-email" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Email
          </label>
          <input
            id="operator-email"
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="glass-input px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          />
        </div>
        <div>
          <label htmlFor="operator-role" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Role
          </label>
          <select
            id="operator-role"
            value={role}
            onChange={(e) => setRole(Number(e.target.value) as pb.ConsoleOperatorRole)}
            className="glass-input px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          >
            <option value={pb.ConsoleOperatorRole.CONSOLE_OPERATOR_ROLE_VIEWER}>
              Viewer
            </option>
            <option value={pb.ConsoleOperatorRole.CONSOLE_OPERATOR_ROLE_ADMIN}>
              Admin
            </option>
          </select>
        </div>
        <button
          type="submit"
          disabled={createOpMutation.isPending}
          className="rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          style={primaryButtonCompactStyle}
        >
          {createOpMutation.isPending ? 'Creating…' : 'Create operator'}
        </button>
      </form>

      {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading operators…</p>}
      {isError && (
        <div className="px-3 py-2" style={{ backgroundColor: DARK, borderLeft: `3px solid ${DANGER}`, borderRadius: '8px' }}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load operators.</p>
        </div>
      )}

      {!isLoading && !isError && data && (
        <div className="flex flex-col gap-3">
          {data.getOperatorsList().map((op) => (
            <div key={op.getId()} className="glass-card p-4">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <span className="font-medium" style={{ color: WHITE }}>{op.getName()}</span>{' '}
                  <span className="text-sm" style={{ color: GREY_400 }}>{op.getEmail()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: DARK, color: GREY_300 }}>
                    {op.getRole() === pb.ConsoleOperatorRole.CONSOLE_OPERATOR_ROLE_ADMIN
                      ? 'admin'
                      : 'viewer'}
                  </span>
                  {op.getDisabled() && (
                    <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'rgba(242, 123, 118, 0.12)', color: DANGER }}>
                      disabled
                    </span>
                  )}
                </div>
              </div>

              <div className="mb-3 flex flex-wrap gap-2">
                <CreateKeyInline
                  operatorId={op.getId()}
                  onCreated={(key) => setShownKey(key)}
                />

                <ConfirmDialog
                  trigger={(open) => (
                    <button
                      type="button"
                      disabled={op.getDisabled()}
                      onClick={open}
                      className="rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
                      style={{ border: `1px solid ${DANGER}`, color: DANGER }}
                    >
                      Disable operator
                    </button>
                  )}
                  title="Disable operator"
                  description="Every API key belonging to this operator stops authenticating on its next use. There is no re-enable RPC in this phase — a fresh operator/key would need to be issued instead."
                  confirmLabel="Disable"
                  destructive
                  onConfirm={async () => {
                    await disableOperator(op.getId())
                    invalidate()
                  }}
                />
              </div>

              <OperatorApiKeys operatorId={op.getId()} />
            </div>
          ))}
        </div>
      )}

      {shownKey && (
        <ShowOnceKeyModal plaintextKey={shownKey} onClose={() => setShownKey(null)} />
      )}
    </div>
  )
}

// OperatorApiKeys lists one operator's API-key metadata (ListApiKeys,
// ADR-118 Phase 5D, #1258 — admin-only) with a working revoke button per
// non-revoked key. Scoped as its own query key so revoking/creating a key
// for one operator only invalidates that operator's list, not every
// operator's.
function OperatorApiKeys({ operatorId }: { operatorId: string }) {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['api-keys', operatorId],
    queryFn: () => listApiKeys({ operatorId }),
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['api-keys', operatorId] })

  if (isLoading) {
    return <p className="text-xs" style={{ color: GREY_500 }}>Loading keys…</p>
  }
  if (isError) {
    return <p className="text-xs" style={{ color: DANGER }}>Failed to load API keys.</p>
  }

  const keys = data?.getKeysList() ?? []
  if (keys.length === 0) {
    return <p className="text-xs" style={{ color: GREY_500 }}>No API keys for this operator.</p>
  }

  return (
    <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${DARK}` }}>
      <table className="w-full min-w-[560px] border-collapse text-xs">
        <thead>
          <tr className="text-left" style={{ borderBottom: `1px solid ${DARK}`, color: GREY_500 }}>
            <th className="px-2 py-1.5 font-medium">Prefix</th>
            <th className="px-2 py-1.5 font-medium">Name</th>
            <th className="px-2 py-1.5 font-medium">Created</th>
            <th className="px-2 py-1.5 font-medium">Last used</th>
            <th className="px-2 py-1.5 font-medium">Expires</th>
            <th className="px-2 py-1.5 font-medium">Status</th>
            <th className="px-2 py-1.5 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const revoked = k.hasRevokedAt()
            return (
              <tr key={k.getId()} style={{ borderBottom: `1px solid ${DARK}` }}>
                <td className="px-2 py-1.5 font-mono" style={{ color: GREY_300 }}>{k.getKeyPrefix()}</td>
                <td className="px-2 py-1.5" style={{ color: GREY_300 }}>{k.getName() || '—'}</td>
                <td className="px-2 py-1.5" style={{ color: GREY_500 }}>
                  {formatTimestamp(k.getCreatedAt())}
                </td>
                <td className="px-2 py-1.5" style={{ color: GREY_500 }}>
                  {formatTimestamp(k.getLastUsedAt())}
                </td>
                <td className="px-2 py-1.5" style={{ color: GREY_500 }}>
                  {k.hasExpiresAt() ? formatTimestamp(k.getExpiresAt()) : 'never'}
                </td>
                <td className="px-2 py-1.5">
                  {revoked ? (
                    <span className="rounded-full px-1.5 py-0.5" style={{ backgroundColor: 'rgba(242, 123, 118, 0.12)', color: DANGER }}>
                      revoked {formatTimestamp(k.getRevokedAt())}
                    </span>
                  ) : (
                    <span className="rounded-full px-1.5 py-0.5" style={{ backgroundColor: 'rgba(74, 222, 128, 0.12)', color: SUCCESS }}>
                      active
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {!revoked && (
                    <ConfirmDialog
                      trigger={(open) => (
                        <button
                          type="button"
                          onClick={open}
                          className="rounded-md px-2 py-0.5 text-xs"
                          style={{ border: `1px solid ${DANGER}`, color: DANGER }}
                        >
                          Revoke
                        </button>
                      )}
                      title="Revoke API key"
                      description={`This immediately invalidates key ${k.getKeyPrefix()}. This cannot be undone — a new key would need to be issued instead.`}
                      confirmLabel="Revoke"
                      destructive
                      onConfirm={async () => {
                        await revokeApiKey(k.getId())
                        invalidate()
                      }}
                    />
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function CreateKeyInline({
  operatorId,
  onCreated,
}: {
  operatorId: string
  onCreated: (plaintextKey: string) => void
}) {
  const queryClient = useQueryClient()
  const [keyName, setKeyName] = useState('')
  const mutation = useMutation({
    mutationFn: () => createApiKey({ operatorId, name: keyName || 'console-ui-key' }),
    onSuccess: (res) => {
      setKeyName('')
      queryClient.invalidateQueries({ queryKey: ['api-keys', operatorId] })
      onCreated(res.getPlaintextKey())
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        mutation.mutate()
      }}
      className="flex items-center gap-1.5"
    >
      <input
        placeholder="key name (e.g. saul-cli)"
        value={keyName}
        onChange={(e) => setKeyName(e.target.value)}
        className="glass-input w-44 px-2 py-1 text-xs"
        style={{ color: WHITE }}
      />
      <button
        type="submit"
        disabled={mutation.isPending}
        className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
        style={{ color: GREY_300 }}
      >
        Create API key
      </button>
    </form>
  )
}
